import React, { useRef, useState } from "react";
import { FullscreenTakeover, LoadingIndicator, Step } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { withClientContext } from "~/apollo/client";
import { useGetEntityDeltasQuery } from "~/graphql/mainHooks";
import { type GetEntityDeltasQuery } from "~/graphql/mainTypes";
import {
  type GetTransferBatchesQueryResult,
  useGetTransferBatchesQuery,
  useStartEntityGlSyncMutation,
} from "~/graphql/supergraphHooks";
import { getErrorMessage } from "~/utils/errors";
import { toGlobalId } from "~/utils/graphql-helpers";
import StepNavigationIndicator from "../StepNavigation";
import ErrorState from "../UpdateTransactionTakeover/States/ErrorState";
import FinishedState from "../UpdateTransactionTakeover/States/FinishedState";
import SyncSettings from "./components/SyncSettings";
import ReviewAndSync from "./ReviewAndSync";

type Props = {
  entityName: string;
  entityId: number;
  arenaId: number;
  arenaName: string;
  gpEntityOverviewUrl: string;
  onExit: () => void;
  arenaSubdomain: string;
  userId: number;
  syncFromTransferHistory: boolean;
};

enum STEP_NAMES {
  configure = "Configure sync settings",
  review = "Review and sync",
  finished = "Finished",
}

type StepRendererType = {
  [
    key in STEP_NAMES.configure | STEP_NAMES.review | STEP_NAMES.finished
  ]: () => React.ReactNode;
};

export type QueryResult = NonNullable<
  GetTransferBatchesQueryResult["data"]
>["entityTransferGlBatchesDetails"];

const TransferOnlySyncTakeover = ({
  entityName,
  entityId,
  arenaId,
  arenaName,
  gpEntityOverviewUrl,
  arenaSubdomain,
  onExit,
  userId,
  syncFromTransferHistory,
}: Props) => {
  const enableResyncTransfer = useFeatureFlag("resyncTransfer", false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const startDateRef = useRef<string | null>(null);
  const endDateRef = useRef<string>("");
  const includeAllTransactionsRef = useRef<boolean>(false);
  const { data: deltaData, loading: deltaLoading } = useGetEntityDeltasQuery({
    context: withClientContext("staff", {
      arenaId,
    }),
    variables: {
      entitiesFilters: {
        ids: [toGlobalId("AccountObjectType", entityId)],
      },
      transferOnly: true,
    },
  }) as { data: GetEntityDeltasQuery; loading: boolean };

  const deltaDateList = deltaData?.investmentEntities?.edges[0]?.node?.deltas.map(
    (delta) => delta.date,
  );

  const { data: batchData, loading: batchDataLoading } = useGetTransferBatchesQuery({
    variables: {
      entityId,
      arenaId,
      isProcessed: false,
    },

    skip: !arenaId || !entityId,
    fetchPolicy: "network-only",
  });
  const glBatchData: QueryResult = batchData
    ? batchData.entityTransferGlBatchesDetails
    : { unsynced: [], history: [] };

  let glBatchDates: string[] = glBatchData.unsynced.map((batch) => `${batch.batchDate}` || "");
  // cross filter gl batch that could be sync with delta date
  glBatchDates = glBatchDates.filter((value) => deltaDateList?.includes(value));
  glBatchDates = glBatchDates.sort((a: string, b: string) => {
    return new Date(a).getTime() - new Date(b).getTime();
  });

  const [currentStep, setCurrentStep] = useState<STEP_NAMES>(STEP_NAMES.configure);

  const goBack = () => {
    setErrorMessage("");
    setCurrentStep(STEP_NAMES.configure);
  };
  const [startSyncMutate] = useStartEntityGlSyncMutation({
    fetchPolicy: "network-only",
    onError: (error: unknown) => {
      setErrorMessage(getErrorMessage(error));
    },
  });

  const handleNext = (
    startDate: string | null,
    endDate: string,
    includeAllTransactions: boolean,
  ) => {
    startDateRef.current = enableResyncTransfer ? startDate : null;
    endDateRef.current = endDate;
    includeAllTransactionsRef.current = includeAllTransactions;
    setCurrentStep(STEP_NAMES.review);
  };
  const handleSync = async () => {
    setCurrentStep(STEP_NAMES.finished);
    const invalidBatchIds = glBatchData.unsynced
      .filter((batch) => {
        return !glBatchDates.includes(`${batch.batchDate}`);
      })
      .map((batch) => batch.id);
    await startSyncMutate({
      variables: {
        input: {
          arenaId: arenaId,
          entityId,
          userId: userId,
          startDate: startDateRef.current,
          endDate: endDateRef.current,
          includeAllTransactions: true,
          includeCapitalCall: true,
          includeCapitalTransaction: true,
          includeDistribution: true,
          includeNetIncome: true,
          excludeGlBatchIds: invalidBatchIds,
          transferOnly: true,
        },
      },
    });
  };
  // step to component mapping
  const stepsRenders: StepRendererType = {
    [STEP_NAMES.configure]: () => (
      <SyncSettings
        entityId={entityId}
        arenaId={arenaId}
        arenaSubdomain={arenaSubdomain}
        entityName={entityName}
        arenaName={arenaName}
        onNext={handleNext}
        glBatchDates={glBatchDates}
        syncFromTransferHistory={syncFromTransferHistory}
      />
    ),
    [STEP_NAMES.review]: () => (
      <ReviewAndSync
        onBack={() => setCurrentStep(STEP_NAMES.configure)}
        onSync={handleSync}
        entityId={entityId}
        arenaId={arenaId}
        endDate={endDateRef.current}
        startDate={startDateRef.current}
        includeAllTransactions={
          enableResyncTransfer && (syncFromTransferHistory || includeAllTransactionsRef.current)
        }
      />
    ),
    [STEP_NAMES.finished]: () => (
      <FinishedState
        entityName={entityName}
        onExit={onExit}
        gpEntityOverviewUrl={gpEntityOverviewUrl}
        arenaName={arenaName}
        isTransfers
      />
    ),
  };

  // steps
  const getReviewStepStatus = () => {
    switch (currentStep) {
      case STEP_NAMES.configure:
        return "inactive";
      case STEP_NAMES.review:
        return "active";
      case STEP_NAMES.finished:
        return "complete";
    }
  };
  const steps: Step[] = [
    {
      name: STEP_NAMES.configure,
      // url is used as key in the .map loop for steps in StepIndicator
      // it is required for React
      url: STEP_NAMES.configure,
      status: currentStep == STEP_NAMES.configure ? "active" : "complete",
      hidden: false,
      index: 1,
    },
    {
      name: STEP_NAMES.review,
      url: STEP_NAMES.review,
      status: getReviewStepStatus(),
      hidden: false,
      index: 2,
    },
  ];

  const title = entityName ? `Sync transfers  ·  ${entityName}` : "Sync transfers";

  const content = (
    <>
      {(batchDataLoading || deltaLoading) && <LoadingIndicator />}
      {!(batchDataLoading || deltaLoading) && !!errorMessage && (
        <ErrorState
          onExit={onExit}
          goBack={goBack}
          entityName={entityName}
          errorMessage={errorMessage}
        />
      )}
      {!(batchDataLoading || deltaLoading) && !errorMessage && stepsRenders[currentStep]()}
    </>
  );

  return (
    // style here make the takeover full screen
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        onExit={() => {
          setCurrentStep(STEP_NAMES.configure);
          onExit();
        }}
        title={title}
      />
      <FullscreenTakeover.Subheader style={{ padding: 0 }}>
        <StepNavigationIndicator steps={steps} />
      </FullscreenTakeover.Subheader>
      <FullscreenTakeover.Body style={{ height: "fit-content" }}>
        {batchDataLoading || deltaLoading ? <LoadingIndicator /> : content}
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};

export default TransferOnlySyncTakeover;
